import {
  ActivityIcon,
  CogIcon,
  DrillIcon,
  DropletsIcon,
  GaugeIcon,
  GraduationCapIcon,
  LayersIcon,
  MapIcon,
  ShieldAlertIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Drilling & Well Engineering Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to drilling and well engineering can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Introduction to Drilling & Well Engineering',
      'Purpose and Functions of a Well',
      'Main Stages of the Well Lifecycle',
      'Basic Well Architecture',
      'Drilling Process Fundamentals',
      'Essential Drilling Terminology',
      'Major Well Components',
      'Introduction to Drilling Systems',
      'Basic Subsurface Concepts',
      'Pressure Fundamentals',
      'Introduction to Well Construction',
      'Well Integrity Fundamentals',
      'Safety and Environmental Awareness',
      'Engineering Roles and Responsibilities',
      'Connections Between Drilling, Completion, and Production',
      'Classroom Applications and Conceptual Examples',
    ],
    result:
      'Your students understand drilling and well engineering as a practical discipline that connects subsurface science, equipment operation, and well integrity — not an impossible list of terms to memorize.',
    icon: DrillIcon,
  },
  {
    number: '2',
    title: 'Well Planning, Geology & Drilling Operations',
    subtitle: 'Everything your students need to know about well planning and operational decision-making',
    context:
      'This is where many students struggle to build the planning and operational foundation. This volume gives you the materials to help them truly understand drilling operations.',
    items: [
      'Well Planning Fundamentals',
      'Geological Information for Drilling',
      'Formation Characteristics',
      'Subsurface Uncertainty',
      'Well Objectives and Design Considerations',
      'Well Trajectory Concepts',
      'Vertical, Directional, and Horizontal Wells',
      'Measured Depth vs. True Vertical Depth',
      'Drilling Program Concepts',
      'Formation Pressure Considerations',
      'Drilling Operational Stages',
      'Data Interpretation During Drilling',
      'Geological and Operational Risks',
      'Planning Under Uncertainty',
      'Communication Between Geology and Engineering Teams',
      'Classroom Applications and Planning Scenarios',
      '15 worked well planning and operational examples',
    ],
    result: 'Your students learn the planning and operational principles that underpin successful well construction.',
    icon: MapIcon,
  },
  {
    number: '3',
    title: 'Drilling Rigs, Equipment & Systems',
    subtitle: 'Explore drilling rigs as interconnected systems of mechanical, hydraulic, and control components',
    context:
      'Drilling rigs and their systems are complex. This volume gives you everything needed so your students grasp rig operations clearly.',
    items: [
      'Major Types of Drilling Rigs',
      'Land Rigs and Mobile Land Rigs',
      'Jack-up Rigs',
      'Semi-submersibles and Drillships',
      'Rig Structure and Layout',
      'Hoisting Systems',
      'Rotary Systems',
      'Drill String Components',
      'Drill Bits',
      'Circulation Systems',
      'Pumps and Hydraulic Equipment',
      'Solids-Control Equipment',
      'Power-Generation Systems',
      'Blowout Preventer Systems',
      'Monitoring and Control Systems',
      'Equipment Maintenance Concepts',
      'Interaction Between Personnel and Equipment',
      'Operational Consequences of Equipment Problems',
      'Rig Safety Concepts',
      'Classroom Applications and Integrated Cases',
      '15 worked drilling rig and equipment examples',
    ],
    result:
      'Your students understand how drilling rigs operate and how their systems integrate to support well construction.',
    icon: CogIcon,
  },
  {
    number: '4',
    title: 'Drilling Fluids & Well Circulation',
    subtitle: 'Understand how drilling fluids and circulation affect drilling performance and well conditions',
    context: 'Drilling fluids are critical to drilling success. This volume makes these systems understandable.',
    items: [
      'Functions of Drilling Fluids',
      'Water-Based Drilling Fluids',
      'Oil-Based and Synthetic-Based Systems',
      'Pneumatic Drilling Systems',
      'Fluid Density',
      'Pressure Relationships',
      'Viscosity and Flow Behavior',
      'Complete Circulation Path',
      'Cuttings Transport',
      'Hole-Cleaning Principles',
      'Solids-Control Systems',
      'Formation–Fluid Interaction',
      'Wellbore Stability',
      'Lost Circulation Concepts',
      'Fluid Monitoring',
      'Operational Indicators',
      'Environmental Considerations',
      'Human Factors and Decision-Making',
      'Classroom Applications',
      'Integrated Drilling-Fluid Cases',
      '12 worked drilling fluid and circulation examples',
    ],
    result:
      'Your students understand how drilling fluids and circulation systems support efficient drilling and well integrity.',
    icon: DropletsIcon,
  },
  {
    number: '5',
    title: 'Casing, Cementing & Well Construction',
    subtitle:
      'Shows how a drilled hole becomes a constructed well through casing, cementing, isolation, and integrity',
    context:
      'Well construction is the foundation of well integrity. This volume makes these concepts clear and applicable.',
    items: [
      'Well Construction Fundamentals',
      'Purpose of Casing',
      'Conductor Casing',
      'Surface Casing',
      'Intermediate Casing',
      'Production Casing',
      'Liners',
      'Casing Design Concepts',
      'Casing Installation Principles',
      'Introduction to Cementing',
      'Cement Placement Concepts',
      'Formation Isolation',
      'Zonal Isolation',
      'Casing and Cement Interaction',
      'Well Integrity Principles',
      'Construction Challenges',
      'Cementing Quality Considerations',
      'Safety and Environmental Considerations',
      'Complete Well-Construction Sequence',
      'Classroom Applications',
      'Integrated Construction Cases',
      '12 worked casing and cementing examples',
    ],
    result:
      'Your students can explain how casing and cementing create the well structure and understand well construction challenges.',
    icon: LayersIcon,
  },
  {
    number: '6',
    title: 'Well Completion & Production Fundamentals',
    subtitle: 'Explains the transition from a constructed well to a functioning production system',
    context:
      'Well completion connects engineering to production. This volume makes these concepts accessible and understandable.',
    items: [
      'Completion Fundamentals',
      'Purpose of Well Completion',
      'Major Completion Types',
      'Production Tubing',
      'Packers',
      'Perforation Concepts',
      'Reservoir-to-Well Connection',
      'Formation Stimulation',
      'Hydraulic Fracturing Concepts',
      'Wellhead Systems',
      'Natural Flow',
      'Introduction to Artificial Lift',
      'Rod Pumping',
      'Electrical Submersible Pumps',
      'Gas Lift',
      'Progressive Cavity Pumps',
      'Production Performance',
      'Well Intervention Concepts',
      'Maintenance Considerations',
      'Completion and Production Challenges',
      'Safety and Environmental Considerations',
      'Classroom Applications',
      'Integrated Completion Cases',
      '12 worked completion and production examples',
    ],
    result:
      'Your students understand how wells transition from construction to production and how different completion systems function.',
    icon: ActivityIcon,
  },
  {
    number: '7',
    title: 'Well Control, Pressure Management & Operational Safety',
    subtitle:
      'Develops conceptual understanding of pressure, barriers, well control, and risk without hazardous field procedures',
    context:
      'Well control is fundamental to drilling safety. This volume develops safety-conscious well engineering professionals.',
    items: [
      'Formation Pressure',
      'Pore Pressure',
      'Hydrostatic Pressure',
      'Bottomhole Pressure',
      'Fracture Pressure',
      'Pressure Windows',
      'Primary Well Control',
      'Secondary Well Control',
      'Well Barriers',
      'Barrier Functions and Integrity',
      'Kick Concepts',
      'Kick Indicators',
      'Blowout Concepts',
      'Blowout Preventer Systems',
      'Pressure Monitoring',
      'Engineering Decision-Making',
      'Well-Control Risk Factors',
      'Human Factors',
      'Risk Assessment Concepts',
      'Incident Learning',
      'Safety Culture',
      'Environmental Consequences',
      'Classroom Applications',
      'Integrated Well-Control Cases',
      '15 practical well control and safety case studies',
    ],
    result:
      'Your students understand that well control and pressure management are built on barrier integrity, risk assessment, and continuous improvement.',
    icon: ShieldAlertIcon,
  },
  {
    number: '8',
    title: 'Drilling Performance, Troubleshooting & Well Problems',
    subtitle:
      'Moves students from understanding drilling operations to interpreting performance and reasoning through problems',
    context:
      'Drilling troubleshooting is an essential professional skill. This volume develops evidence-based diagnostic reasoning.',
    items: [
      'Drilling Performance Fundamentals',
      'Rate of Penetration',
      'Weight on Bit',
      'Rotary Speed',
      'Torque',
      'Pump Pressure',
      'Flow and Returns',
      'Equipment Performance Indicators',
      'Non-Productive Time',
      'Performance Trends',
      'Stuck Pipe Concepts',
      'Wellbore Instability',
      'Lost Circulation',
      'Hole-Cleaning Problems',
      'Torque and Drag',
      'Bit Wear',
      'Equipment and Circulation Problems',
      'Formation-Related Challenges',
      'Data Interpretation',
      'Troubleshooting Methodology',
      'Multiple-Hypothesis Reasoning',
      'Risk Evaluation',
      'Drilling Optimization',
      'Operational Cases',
      'Classroom Troubleshooting Activities',
      '15 worked drilling performance and troubleshooting examples',
    ],
    result:
      'Your students can analyze drilling data, identify operational problems, and develop solutions through systematic reasoning.',
    icon: GaugeIcon,
  },
  {
    number: '9',
    title: 'Teaching Drilling & Well Engineering: Methods, Activities & Classroom Practice',
    subtitle: 'Your complete guide as a drilling and well engineering education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of drilling and well engineering teaching.',
    items: [
      'How to Teach Drilling & Well Engineering',
      'Organizing Technical Content for Students',
      'Teaching Through the Well Lifecycle',
      'Teaching Technical Vocabulary',
      'Teaching Drilling Fundamentals',
      'Teaching Geology and Well Planning',
      'Teaching Rig Systems',
      'Teaching Drilling Fluids',
      'Teaching Casing and Cementing',
      'Teaching Completion and Production',
      'Teaching Pressure and Well Control',
      'Teaching Drilling Performance',
      'Teaching Troubleshooting',
      'Case-Based Learning',
      'Problem-Based Learning',
      'Technical Reasoning Activities',
      'Safety-Based Learning',
      'Classroom Discussion Strategies',
      'Assessment Strategies',
      'Asking Effective Technical Questions',
      'Connecting All 9 Volumes',
      'Integrated Teaching Approaches',
      'Teacher Planning Guidance',
      'Capstone Classroom Applications',
      '10 complete integrated well engineering projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
